/**
 * ApplicationCard Component
 *
 * US-073: Mobile Card Layout for Applications Page
 *
 * A responsive card component for displaying application information on mobile devices.
 * Used in the Applications page to show applications as stacked cards instead of table rows
 * when the viewport is less than 640px (sm breakpoint).
 *
 * Features:
 * - Prominent candidate name with link to profile
 * - Job title display
 * - Color-coded status badge
 * - Score/rating display (when available)
 * - Date applied with relative formatting
 * - Click to navigate to application detail
 * - Reprocess action button
 * - Visual distinction for inactive candidates
 * - Touch-friendly: 44px+ tap targets with adequate padding
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { FileText, Eye, RotateCcw, ExternalLink, CheckSquare, Square } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Card } from './ui/Card';
import { Badge, type BadgeVariant } from './ui/Badge';
import type { ApplicationStatus } from '../types';

// Props interface for the ApplicationCard component
export interface ApplicationCardProps {
  application: {
    id: number;
    srApplicationId?: string;
    candidateId: number;
    candidateFirstName?: string;
    candidateLastName?: string;
    jobId: number;
    jobTitle?: string;
    status: ApplicationStatus;
    srStage: string | null;
    recommendation?: number;
    score?: number;
    createdAt: string;
    errorMessage: string | null;
  };
  /** Whether the candidate is in an inactive stage (hired, rejected, withdrawn) */
  isInactive: boolean;
  /** Callback when view button is clicked */
  onView: (id: number) => void;
  /** Callback when reprocess button is clicked */
  onReprocess: (id: number) => void;
  /** Whether a reprocess operation is in progress */
  isReprocessing: boolean;
  /** Whether this card is selected (for bulk operations) */
  isSelected?: boolean;
  /** Callback when checkbox is toggled */
  onToggleSelect?: (id: number) => void;
  /** Whether selection mode is active */
  selectionMode?: boolean;
}

/**
 * Maps application status to Badge variant for consistent color coding
 */
const getStatusBadgeVariant = (status: ApplicationStatus): BadgeVariant => {
  const statusVariantMap: Record<ApplicationStatus, BadgeVariant> = {
    received: 'info',
    enriching: 'warning',
    enriched: 'primary',
    ai_processing: 'primary',
    ai_complete: 'success',
    synced: 'success',
    error: 'danger',
    stub: 'neutral',
    skipped: 'neutral',
  };
  return statusVariantMap[status] || 'default';
};

/**
 * Gets the color class for the rating based on score value
 */
const getRatingColorClass = (rating: number): string => {
  if (rating >= 4) return 'text-green-600';
  if (rating >= 3) return 'text-yellow-600';
  return 'text-red-600';
};

/**
 * ApplicationCard - Mobile-friendly card view for application data
 *
 * Displays application information in a card format optimized for mobile screens.
 * The entire card is clickable to navigate to the application detail page.
 */
export const ApplicationCard: React.FC<ApplicationCardProps> = ({
  application,
  isInactive,
  onView,
  onReprocess,
  isReprocessing,
  isSelected,
  onToggleSelect,
  selectionMode,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Format candidate name with fallback for loading state
  const candidateName = application.candidateFirstName
    ? `${application.candidateFirstName} ${application.candidateLastName}`
    : 'Candidate Name Loading...';

  return (
    <Card
      variant="elevated"
      className={`
        relative transition-all duration-200 ease-in-out
        hover:shadow-lg
        ${isInactive ? 'bg-gray-50 opacity-60' : 'bg-white'}
        ${isSelected ? 'ring-2 ring-primary-400' : ''}
      `}
    >
      <div className={`p-4 ${selectionMode ? 'pl-12' : ''}`}>
        {/* Checkbox overlay for bulk selection */}
        {selectionMode && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleSelect?.(application.id);
            }}
            className="absolute top-4 left-3 z-10 rounded p-0.5 hover:bg-gray-100"
          >
            {isSelected ? (
              <CheckSquare className="h-5 w-5 text-primary-600" />
            ) : (
              <Square className="h-5 w-5 text-gray-400" />
            )}
          </button>
        )}
        {/* Header: Candidate Name and Actions */}
        <div className="flex items-start justify-between">
          {/* Candidate info with icon and name */}
          <div
            className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
            onClick={() => onView(application.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onView(application.id)}
            aria-label={`View application for ${candidateName}`}
          >
            {/* Application icon */}
            <div
              className={`
                flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg
                transition-colors duration-150
                ${isInactive ? 'bg-gray-200' : 'bg-primary-100'}
              `}
            >
              <FileText
                className={`h-5 w-5 ${isInactive ? 'text-gray-400' : 'text-primary-600'}`}
                aria-hidden="true"
              />
            </div>

            {/* Name and job title */}
            <div className="min-w-0 flex-1">
              {application.candidateId ? (
                <Link
                  to={`/candidates/${application.candidateId}`}
                  className={`
                    font-medium block truncate
                    transition-colors duration-150
                    ${isInactive
                      ? 'text-gray-500 hover:text-gray-700'
                      : 'text-primary-600 hover:text-primary-700'}
                  `}
                  onClick={(e) => e.stopPropagation()} // Prevent card click when clicking link
                >
                  {candidateName}
                </Link>
              ) : (
                <p className={`font-medium truncate ${isInactive ? 'text-gray-500' : 'text-gray-900'}`}>
                  {candidateName}
                </p>
              )}
              <p className="text-sm text-gray-500 truncate">
                {application.jobTitle || `Job #${application.jobId}`}
              </p>
            </div>
          </div>

          {/* Action buttons - 44px min touch targets for mobile */}
          <div className="flex items-center gap-0.5 ml-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView(application.id);
              }}
              className="
                rounded-lg p-2.5 min-h-[44px] min-w-[44px]
                flex items-center justify-center
                text-gray-400
                hover:bg-gray-100 hover:text-gray-600
                active:bg-gray-200
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                transition-colors duration-150
              "
              title="View details"
              aria-label="View application details"
            >
              <Eye className="h-5 w-5" aria-hidden="true" />
            </button>
            {application.srApplicationId && (
              <a
                href={`https://www.smartrecruiters.com/app/people/applications/${application.srApplicationId}/`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="
                  rounded-lg p-2.5 min-h-[44px] min-w-[44px]
                  flex items-center justify-center
                  text-gray-400
                  hover:bg-gray-100 hover:text-gray-600
                  active:bg-gray-200
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                  transition-colors duration-150
                "
                title="View in SmartRecruiters"
                aria-label="View in SmartRecruiters"
              >
                <ExternalLink className="h-5 w-5" aria-hidden="true" />
              </a>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReprocess(application.id);
              }}
              className="
                rounded-lg p-2.5 min-h-[44px] min-w-[44px]
                flex items-center justify-center
                text-gray-400
                hover:bg-gray-100 hover:text-gray-600
                active:bg-gray-200
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                transition-colors duration-150
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              title="Reprocess application"
              aria-label="Reprocess application"
              disabled={isReprocessing}
            >
              <RotateCcw
                className={`h-5 w-5 ${isReprocessing ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        {/* Details Grid - 2 columns for compact display */}
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          {/* Status Badge */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
            <Badge
              variant={getStatusBadgeVariant(application.status)}
              size="sm"
              rounded
            >
              {application.status.replace('_', ' ')}
            </Badge>
          </div>

          {/* SR Stage */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Stage</p>
            <span className={isInactive ? 'text-gray-400 italic' : 'text-gray-900'}>
              {application.srStage || 'N/A'}
            </span>
          </div>

          {/* Rating/Score */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Rating</p>
            {application.recommendation ? (
              <div className="flex items-center gap-1">
                <span className={`font-medium ${getRatingColorClass(application.recommendation)}`}>
                  {application.recommendation}/5
                </span>
                {application.score && (
                  <span className="text-xs text-gray-400">({application.score * 2}%)</span>
                )}
              </div>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>

          {/* Date Applied */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Applied</p>
            <span className="text-gray-600">
              {formatDistanceToNow(new Date(application.createdAt), { addSuffix: true })}
            </span>
          </div>
        </div>

        {/* Error Message - admins see full detail, others see generic message */}
        {application.errorMessage && (
          <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600">
            {isAdmin ? (
              <>
                <span className="font-semibold uppercase tracking-wide text-red-400 mr-1">🔒 Admin only —</span>
                <span className="truncate">{application.errorMessage}</span>
              </>
            ) : (
              'AI review could not be completed.'
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

ApplicationCard.displayName = 'ApplicationCard';

export default ApplicationCard;
